import { createContext, ReactNode, useContext, useEffect, useRef, useState } from "react";
import { AlteracaoCadastral, Documento, Fornecedor, Item, PedidoCompra, User } from "../types";
import { isJwtExpired } from "../utils/functions";
import { api } from "../utils/api";

type AppType = {
  loaded: boolean;
  showModal: boolean;
  modalTitle: string;
  modalBody: ReactNode;
  loading: boolean;
  emailAtivacao: string;
  userAtivacao: string;
  usuario: User;
  fornecedor: Fornecedor;
  itensPedido: Array<Item> | undefined;
  alteracoes: AlteracaoCadastral;
  fatura: Documento;
  pedidos: PedidoCompra[];
  showModalMessage: (title: string, body: ReactNode) => void;
  showModalAnexo: (data: any) => void;
  showModalError: (message: string) => void;
  setUserEmailAtivacao: (user: string, email: string) => void;
  setUsuario: (usuario: User) => void;
  setFornecedor: (forn: Fornecedor) => void;
  closeModal: () => void;
  signOff: () => void;
  setLoading: (active: boolean) => void;
  verificaUser: () => boolean;
  setItens: (itens: Array<Item>) => void;
  setAlteracoes: (alteracao: AlteracaoCadastral) => void;
  setFatura: (fatura: Documento) => void;
  setPedidos: (pedidos: PedidoCompra[]) => void;
};
export const AppContext = createContext({} as AppType);

type AppContextProviderProps = {
  children: ReactNode;
};

export const AppContextProvider = (props: AppContextProviderProps) => {
  const [loading, setLoad] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [userAtivacao, setUserAtivacao] = useState("");
  const [emailAtivacao, setEmailAtivacao] = useState("");
  const [usuario, setUser] = useState<User>({ id: 0 } as User);
  const [modalBody, setModalBody] = useState<ReactNode>();
  const [itensPedido, setItensPedido] = useState<Array<Item>>();
  const [fornecedor, setForn] = useState<Fornecedor>({} as Fornecedor);
  const [fatura, setFat] = useState<Documento>({} as Documento);
  const [loaded, setLoaded] = useState(false);
  const [alteracoes, setAlt] = useState<AlteracaoCadastral>({} as AlteracaoCadastral);
  const [pedidos, setPed] = useState<PedidoCompra[]>([]);
  const [anexoCarta, setAnexoCarta] = useState({} as File);
  const refAnexoCarta = useRef<HTMLInputElement>(null);
  const [sended, setSended] = useState(false);

  useEffect(() => {
    setLoaded(true);
    let JSONObject = localStorage.getItem("usuario");
    if (JSONObject !== null) {
      setUser(JSON.parse(JSONObject));
    }
    JSONObject = localStorage.getItem("fatura");
    if (JSONObject !== null) {
      setFat(JSON.parse(JSONObject));
    }
    JSONObject = localStorage.getItem("itens");
    if (JSONObject !== null) {
      setItens(JSON.parse(JSONObject));
    }
    JSONObject = localStorage.getItem("pedidos");
    if (JSONObject !== null) {
      setPedidos(JSON.parse(JSONObject));
    }
  }, []);

  const setUsuario = (usuario: User) => {
    setUser(usuario);
    localStorage.setItem("usuario", JSON.stringify(usuario));
  };
  const setItens = (itens: Array<Item>) => {
    setItensPedido(itens);
    localStorage.setItem("itens", JSON.stringify(itens));
  };
  const setFornecedor = (forn: Fornecedor) => {
    setForn(forn);
  };
  const setFatura = (fat: Documento) => {
    setFat(fat);
    localStorage.setItem("fatura", JSON.stringify(fat));
  };
  const setPedidos = (peds: PedidoCompra[]) => {
    setPed(peds);
    localStorage.setItem("pedidos", JSON.stringify(peds));
  };
  const setAlteracoes = (alteracao: AlteracaoCadastral) => {
    setAlt(alteracao);
  };
  const setLoading = (active: boolean) => {
    setLoad(active);
  };
  const verificaUser = () => {
    if (!isJwtExpired(usuario.accessToken)) return true;
    else {
      localStorage.clear();
      return false;
    }
  };

  const showModalMessage = (title: string, body: ReactNode) => {
    setModalTitle(title);
    setModalBody(body);
    setShowModal(true);
    setLoad(false);
  };
  const showModalError = (message: string) => {
    setModalTitle("Erro");
    setModalBody(<h2 className="errorMsg">{message}</h2>);
    setShowModal(true);
    setLoad(false);
  };

  const showModalAnexo = (data: any) => {
    data.unshift({ numeroNota: "Selecione", descricao: "", idNota: 0 });
    let formData = new FormData();
    var file = refAnexoCarta.current?.files;
    var nota = "";
    setModalTitle("Anexar Carta de Correção");
    setModalBody(
      <div className="mb-3 ">
        <label className="form-label">Selecione a nota</label>
        <select
          onChange={(e) => (nota = e.target.options[e.target.selectedIndex].value)}
          className={"form-select form-select-sm"}
          placeholder="Selecione"
          size={1}
        >
          {data.map((option: any, index: any) => (
            <option value={option.idNota} key={option.idNota}>
              {option.numeroNota + " " + option.descricao}
            </option>
          ))}
        </select>
        <label className="form-label" style={{ marginTop: "1rem" }}>
          Selecione o arquivo
        </label>
        <input
          type="file"
          className={`${anexoCarta && sended ? "is-invalid" : "borda-cinza"}`}
          ref={refAnexoCarta}
          accept="application/pdf"
          onChange={(e) => {
            e.preventDefault()
            if (refAnexoCarta.current?.files && refAnexoCarta.current.files[0] !== undefined) {
              if (refAnexoCarta.current.files[0].size > 1.5 * 1024 * 1024) {
                window.alert("Tamanho máximo ultrapassado!");
                refAnexoCarta.current.value = "";
              } else file = e.target.files;
            }
          }}
        />
        <small className="form-text text-muted">Tamanho máximo: 1.5MB</small>
        <div className="justify-content-md-end mt-4" style={{ width: "100%" }}>
          <input
            type="button"
            className="btn btn-primary mb-3"
            style={{ display: "block", marginLeft: "auto", marginRight: "0" }}
            value="Salvar"
            onClick={(e) => {
              setLoading(true);
              formData.append("anexo_Carta", file![0]);
              formData.append("idNota", nota);
              if (nota !== "Selecione" && nota !== "") {
                api(usuario)
                  .post("/notasServico/inserirCarta", formData)
                  .then((resp) => {
                    if (resp.status === 200) {
                      setLoading(false);
                      showModalMessage("Sucesso", <h2>A carta de correção foi inserida com sucesso!</h2>);
                    } else {
                      showModalError("Erro ao acessar servidor");
                    }
                  });
              } else {
                showModalError("Selecione uma nota para inserir a carta!");
              }
            }}
          ></input>
        </div>
      </div>
    );
    setShowModal(true);
    setLoad(false);
    return anexoCarta;
  };
  const setUserEmailAtivacao = (user: string, email: string) => {
    setUserAtivacao(user);
    setEmailAtivacao(email);
  };
  const signOff = () => {
    setUser({} as User);
  };
  const closeModal = () => setShowModal(false);
  useEffect(() => {}, []);
  return (
    <AppContext.Provider
      value={{
        showModal,
        modalTitle,
        modalBody,
        showModalMessage,
        showModalAnexo,
        closeModal,
        emailAtivacao,
        userAtivacao,
        setUserEmailAtivacao,
        showModalError,
        usuario,
        setUsuario,
        signOff,
        loading,
        setLoading,
        verificaUser,
        loaded,
        itensPedido,
        setItens,
        fornecedor,
        setFornecedor,
        alteracoes,
        setAlteracoes,
        fatura,
        setFatura,
        pedidos,
        setPedidos,
      }}
    >
      {props.children}
    </AppContext.Provider>
  );
};

const useApp = () => {
  const value = useContext(AppContext);
  return value;
};

export default useApp;
